import { useEffect, useState } from "react"
import styles from "./games.list.module.css"

type SortField = "name" | "price" | "condition" | "language" | "message_date";
type SortDirection = 'asc' | 'desc' | 'unknown';

const daySeconds = 86400;

export interface Game {
    id: string;
    name: string;
    price: number;
    original_text: string;
    condition: string;
    description: string;
    language: string;
    message_date: number;
}

interface CheckedOption {
    value: string;
    checked: boolean;
}

const startOfDay = (epoch: number): number => {
    const date = new Date(epoch);
    date.setHours(0, 0, 0, 0);
    return date.getTime() / 1000;
};

export function GamesList() {
    const [games, setGames] = useState<Game[]>([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isForbidden, setIsForbidden] = useState(false);
    const [sortField, setSortField] = useState<SortField>('message_date');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [dateToLoad, setDateToLoad] = useState<number>(startOfDay(Date.now()));
    const [conditionOptions, setConditionOptions] = useState<CheckedOption[]>([]);
    const [languageOptions, setLanguageOptions] = useState<CheckedOption[]>([]);

    useEffect(() => {
        setIsLoaded(false);
        setError(null);
        setIsForbidden(false);

        const fetchGames = async () => {
            try {
                const token = localStorage.getItem("token");

                const response = await fetch(`/api/games?from=${dateToLoad}&to=${dateToLoad + daySeconds}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

                if (response.status === 403) {
                    setIsForbidden(true);
                    return;
                }

                if (!response.ok) {
                    setError('Failed to fetch games');
                    return;
                }

                const data = await response.json() as Game[];
                setGames(data);

                const conditionsSet = new Set<string>();
                const languagesSet = new Set<string>();

                for (const game of data) {
                    conditionsSet.add(game.condition ?? "unknown");
                    languagesSet.add(game.language ?? "Unknown");
                }

                setConditionOptions(
                    Array.from(conditionsSet)
                        .map(x => ({ value: x, checked: true }))
                        .sort((a, b) => a.value.localeCompare(b.value)));

                setLanguageOptions(
                    Array.from(languagesSet)
                        .map(x => ({ value: x, checked: true }))
                        .sort((a, b) => a.value.localeCompare(b.value)));
            }
            catch (err) {
                console.error(err);
                setError('Failed to parse games');
            }
            finally {
                setIsLoaded(true);
            }
        }

        fetchGames();
    }, [dateToLoad]);

    if (isForbidden) {
        return <p>You are forbidden from viewing the games.</p>
    }

    if (error) {
        return <p>Error: {error}</p>;
    }

    if (!isLoaded) {
        return <p>Loading...</p>;
    }

    const filteredGames = games.filter(x => {
        const langConfig = languageOptions.find(lan => lan.value === (x.language ?? "Unknown"));

        if (!langConfig?.checked) {
            return false;
        }

        const conditionConfig = conditionOptions.find(cond => cond.value === (x.condition ?? "unknown"));

        if (!conditionConfig?.checked) {
            return false;
        }

        return true;
    })

    const sortedGames = filteredGames.sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        const modifier = sortDirection === 'asc' ? 1 : -1;
        return aVal < bVal ? -modifier : aVal > bVal ? modifier : 0;
    });

    const handleSort = (field: SortField) => {
        if (field === sortField) {
            setSortDirection(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    }

    return (
        <div className={styles.page}>
            <div className={styles.wrapper}>
                <table className={styles.table}>
                    <caption>Games sold on {new Date(dateToLoad * 1000).toLocaleDateString("en-GB")}</caption>
                    <thead>
                        <tr>
                            <HeaderWithFilteringAndSorting
                                fieldLabel="Added"
                                handleSort={() => handleSort("message_date")}
                                sortDirection={sortField === "message_date" ? sortDirection : "unknown"} />
                            <HeaderWithFilteringAndSorting
                                fieldLabel="Name"
                                handleSort={() => handleSort("name")}
                                sortDirection={sortField === "name" ? sortDirection : "unknown"} />
                            <HeaderWithFilteringAndSorting
                                fieldLabel="Language"
                                handleSort={() => handleSort("language")}
                                sortDirection={sortField === "language" ? sortDirection : "unknown"}
                                options={languageOptions}
                                setOptions={(options) => setLanguageOptions(options)} />
                            <HeaderWithFilteringAndSorting
                                fieldLabel="Price"
                                handleSort={() => handleSort("price")}
                                sortDirection={sortField === "price" ? sortDirection : "unknown"} />
                            <HeaderWithFilteringAndSorting
                                fieldLabel="Condition"
                                handleSort={() => handleSort("condition")}
                                sortDirection={sortField === "condition" ? sortDirection : "unknown"}
                                options={conditionOptions}
                                setOptions={setConditionOptions} />
                            <th className={styles.colWide}>Description</th>
                            <th className={styles.colOriginal}>Original Text</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedGames.map(game => (
                            <tr key={game.id}>
                                <td>{new Date(game.message_date * 1000).toLocaleTimeString()}</td>
                                <td>{game.name}</td>
                                <td>{game.language}</td>
                                <td>{game.price}</td>
                                <td>{game.condition}</td>
                                <td>{game.description}</td>
                                <td>
                                    <details>
                                        <summary>Original Post</summary>
                                        {game.original_text}
                                    </details>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className={styles.navigation}>
                <button onClick={() => setDateToLoad(dateToLoad - daySeconds)}>Previous Day</button>
                {
                    dateToLoad < startOfDay(Date.now()) && (
                        <button onClick={() => setDateToLoad(dateToLoad + daySeconds)}>Next Day</button>
                    )
                }
            </div>
        </div >
    );
}

function HeaderWithFilteringAndSorting(
    { fieldLabel, sortDirection = 'unknown', handleSort, options, setOptions }: {
        fieldLabel: string;
        sortDirection?: SortDirection;
        handleSort: () => void;
        options?: CheckedOption[];
        setOptions?: (options: CheckedOption[]) => void;
    }) {
    const [menuVisible, setMenuVisible] = useState<boolean>(false);

    const menuElement = menuVisible && options && setOptions && (
        <ul>
            <li key="all">
                <input
                    id={`${fieldLabel}-all`}
                    type="checkbox"
                    checked={!options.some(x => !x.checked)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const copyOptions = [...options];

                        const newValue = e.target.checked;

                        for (const option of copyOptions) {
                            option.checked = newValue;
                        }

                        setOptions(copyOptions);
                    }}
                />
                <label htmlFor="all">(all)</label>
            </li>
            {options.map(x => (
                <li key={x.value}>
                    <input
                        id={`${fieldLabel}-${x.value}`}
                        type="checkbox"
                        checked={x.checked}
                        onChange={(e) => {
                            setOptions(options.map(opt =>
                                opt.value === x.value ? { ...opt, checked: e.target.checked } : opt
                            ));
                        }}
                    />
                    <label htmlFor={x.value}>{x.value}</label>
                </li>
            ))}
        </ul>);

    return (
        <th className={styles.sortable}>
            <p><span onClick={() => {
                if (!options) {
                    return;
                }
                setMenuVisible(!menuVisible);
            }
            }>{fieldLabel}</span><SortIcon sortDirection={sortDirection} handleSort={handleSort} /></p>
            {menuElement}
        </th>
    );
}

const SortIcon = ({ sortDirection, handleSort }: {
    sortDirection: SortDirection,
    handleSort: () => void;
}) => {
    let caption = '';

    switch (sortDirection) {
        case 'asc':
            caption = '↓'
            break;
        case 'desc':
            caption = '↑'
            break;
        default:
            caption = '↕'
    }

    return <span className={styles.sortIcon} onClick={handleSort}>{caption}</span>;
};